import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Collapse, Fade, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { ReusableCustomQuestionGroup } from "../../../shared/interfaces/ReusableCustomQuestionGroup";
import { AddButton } from "../../../shared/FormLayout/IconButtons/AddButton";
import { CustomQuestionGroupCard } from "./CustomQuestionGroupCard";
import { NewCustomQuestionGroupCard } from "./NewCustomQuestionGroupCard";

interface Props {
  customerId: number;
  customQuestionGroups: ReusableCustomQuestionGroup[] | undefined;
  isLoading: boolean;
  canEditWorkflow?: boolean;
  onSuccess: (successMessage: string) => void;
  onError: (errorMessage: string) => void;
}

export function ReusableCustomQuestionGroups(props: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { customQuestionGroups, customerId, isLoading, canEditWorkflow, onSuccess, onError } = props;

  const [newQuestionGroupCardShowing, setNewQuestionGroupCardShowing] = useState(false);

  function handleCreateQuestionGroupSuccess(successMessage: string) {
    setNewQuestionGroupCardShowing(false);
    onSuccess?.(successMessage);
  }

  if (isLoading)
    return (
      <>
        <Fade
          in={isLoading}
          style={{
            transitionDelay: isLoading ? "800ms" : "0ms",
          }}
        >
          <Stack spacing={2} margin={2}>
            {[...Array(2).keys()].map((i) => (
              <Stack key={`skeleton-header-${i}`} spacing={1} margin={2}>
                <Skeleton variant="rectangular" height={70} />
                {[...Array(3).keys()].map((i) => (
                  <Box px={3} key={`skeleton-row-${i}`}>
                    <Skeleton variant="rectangular" height={40} />
                  </Box>
                ))}
              </Stack>
            ))}
          </Stack>
        </Fade>
      </>
    );

  return (
    <Stack>
      {canEditWorkflow && (
        <Box display="flex" justifyContent="center">
          <AddButton
            onClick={() => setNewQuestionGroupCardShowing(true)}
            tooltip={t("customQuestionLibrary.addQuestionGroupToLibrary")}
          />
        </Box>
      )}
      {(customQuestionGroups && customQuestionGroups.length > 0) || newQuestionGroupCardShowing ? (
        <Paper
          variant="outlined"
          sx={{
            pb: 2,
            px: 4,
            backgroundColor: theme.palette.darkBackground.main,
          }}
        >
          <Stack spacing={3}>
            <Collapse in={newQuestionGroupCardShowing}>
              <NewCustomQuestionGroupCard
                customerId={customerId}
                onCreateGroupSuccess={handleCreateQuestionGroupSuccess}
                onCreateGroupError={onError}
                onCancelButtonClick={() => setNewQuestionGroupCardShowing(false)}
                sx={{ mt: 2 }}
                canEditWorkflow={canEditWorkflow}
              />
            </Collapse>
            {customQuestionGroups?.map((questionGroup) => (
              <CustomQuestionGroupCard
                key={`questionGroup-${questionGroup.id}`}
                customerId={customerId}
                customQuestionGroup={questionGroup}
                onSuccess={onSuccess}
                onError={onError}
                displayMode="edit"
                canEditWorkflow={canEditWorkflow}
              />
            ))}
          </Stack>
        </Paper>
      ) : (
        <Box display="flex" justifyContent="center" my={2}>
          <Typography variant="caption" noWrap>
            {t("customQuestions.customQuestionGroupsEmpty")}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
